// src/components/ml/TrainingAutopilotPanel.js
// AI Intelligence Center → Training & Autopilot: train any model now, watch runs live (stage,
// progress, log, decision), and tune the autopilot policy of each model.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
  DrawerOverlay, Flex, FormControl, FormHelperText, FormLabel, Heading, HStack, Icon, IconButton, NumberInput, NumberInputField,
  Progress, Select, SimpleGrid, Skeleton, Switch, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack, useColorModeValue,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { MdAutoMode, MdCancel, MdModelTraining, MdPlayArrow, MdRefresh, MdSave } from 'react-icons/md';
import mlOpsService from '../../services/mlOpsService';
import { useMlEvents } from '../../hooks/useMlOps';
import { DECISION_COLOR, DECISION_LABEL, JOB_COLOR, TRIGGER_LABEL, fmtDate, timeAgo } from './mlFormat';

const duration = (j) => {
  if (!j.started_at) return '—';
  const end = j.finished_at ? new Date(j.finished_at) : new Date();
  const s = Math.max(0, Math.round((end - new Date(j.started_at)) / 1000));
  return s < 90 ? `${s}s` : `${Math.round(s / 60)} min`;
};

function PolicyCard({ m, canManage, onSaved }) {
  const toast = useToast();
  const [draft, setDraft] = useState(m.policy);
  const [saving, setSaving] = useState(false);
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  useEffect(() => { setDraft(m.policy); }, [m.policy]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(m.policy);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const ratio = m.metric.format === 'ratio';

  const save = async () => {
    setSaving(true);
    try {
      await mlOpsService.setPolicy(m.key, {
        enabled: draft.enabled, autoPromote: draft.autoPromote,
        minNewRecords: Number(draft.minNewRecords), minIntervalHours: Number(draft.minIntervalHours),
        improvementThreshold: Number(draft.improvementThreshold), keepArchived: Number(draft.keepArchived),
        safeguard: draft.safeguard === '' || draft.safeguard === null || draft.safeguard === undefined ? null : Number(draft.safeguard),
      });
      toast({ title: `${m.name}: autopilot policy saved`, status: 'success', duration: 2500 });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Could not save the policy', description: e.message, status: 'error', duration: 5000 });
    } finally { setSaving(false); }
  };

  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
      <Flex justify="space-between" align="center" mb={3}>
        <Heading size="sm">{m.name}</Heading>
        <HStack>
          <Text fontSize="xs" color={muted}>Autopilot</Text>
          <Switch colorScheme="purple" isChecked={Boolean(draft.enabled)} onChange={e => set('enabled', e.target.checked)} isDisabled={!canManage} aria-label={`Autopilot for ${m.name}`} />
        </HStack>
      </Flex>
      <Text fontSize="xs" color={m.autopilot?.ready ? 'green.400' : muted} mb={3}>{m.autopilot?.reason}</Text>
      <SimpleGrid columns={2} spacing={3}>
        <FormControl size="sm">
          <FormLabel fontSize="xs" mb={1}>New records before retrain</FormLabel>
          <NumberInput size="sm" min={1} value={draft.minNewRecords ?? ''} onChange={v => set('minNewRecords', v)} isDisabled={!canManage}><NumberInputField /></NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Minimum hours between runs</FormLabel>
          <NumberInput size="sm" min={0} value={draft.minIntervalHours ?? ''} onChange={v => set('minIntervalHours', v)} isDisabled={!canManage}><NumberInputField /></NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Improvement needed</FormLabel>
          <NumberInput size="sm" min={0} max={1} step={0.005} precision={3} value={draft.improvementThreshold ?? ''} onChange={v => set('improvementThreshold', v)} isDisabled={!canManage}><NumberInputField /></NumberInput>
          <FormHelperText fontSize="2xs">{ratio ? 'absolute, 0.02 = +2 points' : 'relative, 0.02 = 2 % lower error'}</FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Safeguard ({m.metric.higherIsBetter ? 'minimum' : 'maximum'} {m.metric.label})</FormLabel>
          <NumberInput size="sm" min={0} step={ratio ? 0.01 : 1} value={draft.safeguard ?? ''} onChange={v => set('safeguard', v)} isDisabled={!canManage}><NumberInputField placeholder="none" /></NumberInput>
          <FormHelperText fontSize="2xs">{ratio ? '0.60 = 60 %' : 'empty = no limit'}</FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Archived versions to keep</FormLabel>
          <NumberInput size="sm" min={5} max={1000} value={draft.keepArchived ?? 50} onChange={v => set('keepArchived', v)} isDisabled={!canManage}><NumberInputField /></NumberInput>
        </FormControl>
        <FormControl display="flex" alignItems="center" pt={5}>
          <Switch size="sm" colorScheme="green" isChecked={Boolean(draft.autoPromote)} onChange={e => set('autoPromote', e.target.checked)} isDisabled={!canManage} mr={2} id={`ap-${m.key}`} />
          <FormLabel htmlFor={`ap-${m.key}`} fontSize="xs" mb={0}>Promote winners automatically</FormLabel>
        </FormControl>
      </SimpleGrid>
      {canManage && (
        <Flex justify="flex-end" mt={3} gap={2}>
          {dirty && <Button size="sm" variant="ghost" onClick={() => setDraft(m.policy)}>Reset</Button>}
          <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdSave} />} onClick={save} isDisabled={!dirty} isLoading={saving}>Save policy</Button>
        </Flex>
      )}
    </Box>
  );
}

export default function TrainingAutopilotPanel({ overview: ov, canManage, onChanged, onTrain, trainingKey }) {
  const toast = useToast();
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [live, setLive] = useState({}); // jobId → {progress, stage}
  const [openJob, setOpenJob] = useState(null);
  const [busy, setBusy] = useState(null);
  const drawer = useDisclosure();
  const mounted = useRef(true);
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const logBg = useColorModeValue('gray.50', 'blackAlpha.400');

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const names = useMemo(() => Object.fromEntries((ov?.models || []).map(m => [m.key, m.name])), [ov]);

  const loadJobs = useCallback(async () => {
    try {
      const j = await mlOpsService.listJobs({ modelType: filter || undefined, limit: 25 });
      if (mounted.current) setJobs(j);
    } catch (e) {
      if (mounted.current) toast({ title: 'Could not load training runs', description: e.message, status: 'error', duration: 4000 });
    } finally { if (mounted.current) setJobsLoading(false); }
  }, [filter, toast]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const openJobId = openJob?.id;
  const reloadOpenJob = useCallback(async () => {
    if (!openJobId) return;
    try { const j = await mlOpsService.getJob(openJobId); if (mounted.current) setOpenJob(j); } catch (_) { /* keep last */ }
  }, [openJobId]);

  const timer = useRef(null);
  useMlEvents(useCallback((ev) => {
    if (ev.event === 'job_update' && ev.jobId) {
      setLive(l => ({ ...l, [ev.jobId]: { progress: ev.progress ?? l[ev.jobId]?.progress, stage: ev.stage || l[ev.jobId]?.stage } }));
      return;
    }
    if (['job_queued', 'job_finished', 'model_promoted', 'model_rolled_back'].includes(ev.event)) {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { loadJobs(); reloadOpenJob(); }, 600);
    }
  }, [loadJobs, reloadOpenJob]));
  useEffect(() => () => clearTimeout(timer.current), []);

  // poll the open run's log while it is active (websocket only carries progress)
  useEffect(() => {
    if (!openJob || !['queued', 'running'].includes(openJob.status)) return undefined;
    const t = setInterval(reloadOpenJob, 3000);
    return () => clearInterval(t);
  }, [openJob, reloadOpenJob]);

  const act = async (kind, fn, okMsg) => {
    setBusy(kind);
    try {
      const r = await fn();
      if (okMsg) toast({ title: typeof okMsg === 'function' ? okMsg(r) : okMsg, status: 'success', duration: 3500 });
      onChanged?.(); loadJobs();
      return r;
    } catch (e) {
      toast({ title: 'Action failed', description: e.message, status: 'error', duration: 5000 });
      return null;
    } finally { if (mounted.current) setBusy(null); }
  };

  const showJob = async (j) => {
    setOpenJob(j); drawer.onOpen();
    try { const full = await mlOpsService.getJob(j.id); if (mounted.current) setOpenJob(full); } catch (_) { /* list row is enough */ }
  };

  const ap = ov?.autopilot;
  return (
    <VStack align="stretch" spacing={4}>
      <Flex bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} gap={4} align="center" wrap="wrap">
        <HStack spacing={3} flex="1" minW="260px">
          <Icon as={MdAutoMode} boxSize={7} color={ap?.enabled ? 'green.400' : 'gray.400'} />
          <Box>
            <Heading size="sm">Autopilot {ap?.enabled ? 'is on' : 'is off'}</Heading>
            <Text fontSize="xs" color={muted}>
              Checks every model {ap?.cron === '*/15 * * * *' ? 'every 15 minutes' : `on "${ap?.cron || '—'}"`} and after dispatch feedback.
              {ap?.lastSweep ? ` Last check ${timeAgo(ap.lastSweep.at)} (${ap.lastSweep.reason}).` : ' Not run yet.'}
            </Text>
          </Box>
        </HStack>
        {canManage && (
          <HStack spacing={2} wrap="wrap">
            <Switch colorScheme="green" isChecked={Boolean(ap?.enabled)} aria-label="Autopilot for all models"
              onChange={e => act('global', () => mlOpsService.setAutopilot(e.target.checked), e.target.checked ? 'Autopilot switched on' : 'Autopilot switched off')} isDisabled={busy === 'global' || !ov} />
            <Button size="sm" leftIcon={<Icon as={MdPlayArrow} />} onClick={() => act('sweep', () => mlOpsService.runAutopilot(), (r) => {
              if (r?.skipped) return r.reason;
              const started = Object.values(r?.results || {}).filter(x => x.jobId).length;
              return started ? `Autopilot started ${started} training run(s)` : 'Autopilot check done — no model needs retraining';
            })} isLoading={busy === 'sweep'}>Run check now</Button>
            <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdModelTraining} />} onClick={() => act('all', () => mlOpsService.trainAll(), r => `Started ${(r || []).filter(j => !j.alreadyActive).length} training run(s)`)} isLoading={busy === 'all'}>Train all models</Button>
          </HStack>
        )}
      </Flex>

      {!canManage && <Alert status="info" borderRadius="lg"><AlertIcon /><AlertDescription fontSize="sm">You can view training and autopilot. Supervisors, managers and admins can train models and change policies.</AlertDescription></Alert>}

      {(ov?.models || []).some(m => m.activeJob) && (
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Heading size="sm" mb={3}>Running now</Heading>
          <VStack align="stretch" spacing={3}>
            {ov.models.filter(m => m.activeJob).map(m => {
              const l = live[m.activeJob.id] || {};
              const progress = l.progress ?? m.activeJob.progress;
              return (
                <Box key={m.key}>
                  <Flex justify="space-between" fontSize="sm" mb={1}>
                    <Text fontWeight="600">{m.name} <Badge ml={2} colorScheme={JOB_COLOR[m.activeJob.status]}>{m.activeJob.status}</Badge></Text>
                    <Text>{progress}%</Text>
                  </Flex>
                  <Progress value={progress} size="sm" colorScheme="blue" hasStripe isAnimated borderRadius="full" />
                  <Text fontSize="xs" color={muted} mt={1}>{l.stage || m.activeJob.stage}</Text>
                </Box>
              );
            })}
          </VStack>
        </Box>
      )}

      <Box>
        <Heading size="sm" mb={3}>Train & autopilot policy per model</Heading>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {!ov ? [0, 1, 2, 3].map(i => <Skeleton key={i} h="280px" borderRadius="xl" />) : ov.models.map(m => (
            <VStack key={m.key} align="stretch" spacing={2}>
              <PolicyCard m={m} canManage={canManage} onSaved={onChanged} />
              {canManage && (
                <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<Icon as={MdModelTraining} />} onClick={() => onTrain(m.key)} isLoading={trainingKey === m.key} isDisabled={Boolean(m.activeJob)}>
                  {m.activeJob ? 'Training…' : `Train ${m.name} now`}
                </Button>
              )}
            </VStack>
          ))}
        </SimpleGrid>
      </Box>

      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} overflowX="auto">
        <Flex mb={3} align="center" gap={3} wrap="wrap">
          <Heading size="sm">Training runs</Heading>
          <Box flex="1" />
          <Select size="sm" maxW="220px" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filter by model">
            <option value="">All models</option>
            {(ov?.models || []).map(m => <option key={m.key} value={m.key}>{m.name}</option>)}
          </Select>
          <Tooltip label="Refresh" hasArrow><IconButton aria-label="Refresh runs" size="sm" icon={<Icon as={MdRefresh} />} onClick={loadJobs} /></Tooltip>
        </Flex>
        <Table size="sm">
          <Thead><Tr><Th>Model</Th><Th>Status</Th><Th>Outcome</Th><Th>Trigger</Th><Th isNumeric>Records</Th><Th>Started</Th><Th>Took</Th><Th /></Tr></Thead>
          <Tbody>
            {jobsLoading ? [0, 1, 2].map(i => <Tr key={i}><Td colSpan={8}><Skeleton h="16px" /></Td></Tr>) : jobs.map(j => {
              const l = live[j.id];
              return (
                <Tr key={j.id} _hover={{ cursor: 'pointer', bg: logBg }} onClick={() => showJob(j)}>
                  <Td fontSize="sm">{names[j.model_type] || j.model_type}</Td>
                  <Td>
                    <Badge colorScheme={JOB_COLOR[j.status]}>{j.status}</Badge>
                    {['queued', 'running'].includes(j.status) && <Progress mt={1} value={l?.progress ?? j.progress} size="xs" colorScheme="blue" borderRadius="full" />}
                  </Td>
                  <Td><Tooltip label={j.error || j.stage || ''} hasArrow>{j.decision ? <Badge variant="subtle" colorScheme={DECISION_COLOR[j.decision]}>{DECISION_LABEL[j.decision]}</Badge> : <Text fontSize="xs" noOfLines={1} maxW="220px">{j.error || l?.stage || j.stage}</Text>}</Tooltip></Td>
                  <Td fontSize="xs">{TRIGGER_LABEL[j.trigger_source] || j.trigger_source}{j.requested_by_name && j.trigger_source === 'manual' ? ` · ${j.requested_by_name}` : ''}</Td>
                  <Td isNumeric fontSize="xs">{j.data_points?.toLocaleString?.() ?? '—'}</Td>
                  <Td fontSize="xs">{fmtDate(j.started_at || j.created_at)}</Td>
                  <Td fontSize="xs">{duration(j)}</Td>
                  <Td onClick={e => e.stopPropagation()}>
                    {canManage && j.status === 'queued' && (
                      <Tooltip label="Cancel queued run" hasArrow>
                        <IconButton aria-label="Cancel run" size="xs" variant="ghost" icon={<Icon as={MdCancel} />} onClick={() => act(`cancel:${j.id}`, () => mlOpsService.cancelJob(j.id), 'Run cancelled')} isLoading={busy === `cancel:${j.id}`} />
                      </Tooltip>
                    )}
                  </Td>
                </Tr>
              );
            })}
            {!jobsLoading && !jobs.length && <Tr><Td colSpan={8}><Text fontSize="sm" color={muted}>No training runs yet.</Text></Td></Tr>}
          </Tbody>
        </Table>
      </Box>

      <Drawer isOpen={drawer.isOpen} onClose={() => { drawer.onClose(); setOpenJob(null); }} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">{openJob ? `${names[openJob.model_type] || openJob.model_type} — training run` : 'Training run'}</DrawerHeader>
          <DrawerBody py={4}>
            {!openJob ? <Skeleton h="200px" /> : (
              <VStack align="stretch" spacing={4}>
                <HStack spacing={2} wrap="wrap">
                  <Badge colorScheme={JOB_COLOR[openJob.status]}>{openJob.status}</Badge>
                  {openJob.decision && <Badge colorScheme={DECISION_COLOR[openJob.decision]}>{DECISION_LABEL[openJob.decision]}</Badge>}
                  <Badge variant="outline">{TRIGGER_LABEL[openJob.trigger_source] || openJob.trigger_source}</Badge>
                  {openJob.model_version && <Badge variant="outline" fontFamily="mono">v{openJob.model_version}</Badge>}
                </HStack>
                {['queued', 'running'].includes(openJob.status) && <Progress value={live[openJob.id]?.progress ?? openJob.progress} hasStripe isAnimated borderRadius="full" />}
                <Text fontSize="sm">{openJob.stage}</Text>
                {openJob.error && <Alert status="error" borderRadius="md"><AlertIcon /><AlertDescription fontSize="sm">{openJob.error}</AlertDescription></Alert>}
                {openJob.result?.verdict && (
                  <Alert status={openJob.decision === 'promoted' ? 'success' : openJob.decision === 'rejected_safeguard' ? 'error' : 'info'} borderRadius="md">
                    <AlertIcon /><AlertDescription fontSize="sm">{openJob.result.verdict.reason}</AlertDescription>
                  </Alert>
                )}
                {openJob.result?.metrics && (
                  <Box>
                    <Text fontSize="xs" color={muted} mb={1}>Metrics reported by the ML engine</Text>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                      {Object.entries(openJob.result.metrics).filter(([, v]) => typeof v === 'number').map(([k, v]) => (
                        <Box key={k} borderWidth="1px" borderColor={border} borderRadius="md" p={2}><Text fontSize="2xs" color={muted}>{k}</Text><Text fontWeight="700">{Math.abs(v) <= 1 ? v.toFixed(3) : v.toFixed(2)}</Text></Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xs" color={muted} mb={1}>Run log</Text>
                  <Box bg={logBg} borderRadius="md" p={3} fontFamily="mono" fontSize="xs" maxH="360px" overflowY="auto">
                    {(openJob.log || []).map((l, i) => (
                      <Text key={i} color={l.level === 'error' ? 'red.400' : l.level === 'warn' ? 'orange.400' : undefined} whiteSpace="pre-wrap">
                        {new Date(l.t).toLocaleTimeString()} {l.msg}
                      </Text>
                    ))}
                    {!openJob.log?.length && <Text>No log lines yet.</Text>}
                  </Box>
                </Box>
                <Text fontSize="xs" color={muted}>Run {openJob.id} · created {fmtDate(openJob.created_at)}{openJob.finished_at ? ` · finished ${fmtDate(openJob.finished_at)}` : ''}</Text>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
}
